import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import qs from 'qs';
import { Helmet } from 'react-helmet';

import { Container } from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { GoogleAdsCampaignConfigItem, listGoogleAdsCampaignsConfig } from '~/services/PublicOfferFeedService';

import GoogleAdsCampaignsConfigTab from './GoogleAdsCampaignsConfigTab';

type CampaignType = 'offer' | 'search' | undefined;

function GoogleAdsCampaignsConfigContainer() {
  const { enqueueSnackbar } = useSnackbar();

  const [data, setData] = useState<Array<GoogleAdsCampaignConfigItem>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pageNum, setPageNum] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  const [customerId, setCustomerId] = useState<string>('');
  const [campaignId, setCampaignId] = useState<string>('');
  const [campaignType, setCampaignType] = useState<CampaignType>(undefined);
  const [enabled, setEnabled] = useState<boolean>(undefined);

  const [totalCount, setTotalCount] = useState(0);

  const setQueryParams = useCallback(() => {
    const searchParams = qs.stringify({
      page_num: pageNum.toString(),
      page_size: pageSize.toString(),
      customer_id: customerId || undefined,
      campaign_id: campaignId || undefined,
      campaign_type: campaignType || undefined,
      enabled: enabled == true ? enabled : undefined,
    });
    window.history.replaceState({}, '', `${window.location.pathname}?${searchParams}`);
  }, [pageNum, pageSize, customerId, campaignId, campaignType, enabled]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await listGoogleAdsCampaignsConfig({
        page: pageNum,
        limit: pageSize,
        customerId: customerId || undefined,
        campaignId: campaignId || undefined,
        campaignType: campaignType || undefined,
        enabled: enabled == true ? enabled : undefined,
      });
      const result = response?.result;
      setData(result?.campaigns ?? []);
      setTotalCount(Number(result?.count ?? 0));
      setIsLoading(false);
      setQueryParams();
    } catch (error) {
      enqueueSnackbar('Failed to fetch Google Ads Campaigns Config', { variant: 'error' });
      setIsLoading(false);
    }
  }, [pageNum, pageSize, customerId, campaignId, campaignType, setQueryParams, enqueueSnackbar, enabled]);

  useEffect(() => {
    const timer = setTimeout(() => fetchData(), 300);
    return () => clearTimeout(timer);
  }, [pageNum, pageSize, customerId, campaignId, campaignType, fetchData]);

  const onPageChange = useCallback(
    (page: number, pageSize: number) => {
      setPageNum(page);
      setPageSize(pageSize);
      fetchData();
    },
    [fetchData],
  );

  return (
    <Container>
      <Helmet>
        <title>Google Ads Campaigns Config</title>
      </Helmet>
      <PageSubheader title={`Google Ads Campaigns Config [${data?.length} of ${totalCount}]`} />

      <GoogleAdsCampaignsConfigTab
        customerId={customerId}
        setCustomerId={setCustomerId}
        campaignId={campaignId}
        setCampaignId={setCampaignId}
        campaignType={campaignType}
        setCampaignType={setCampaignType}
        enabled={enabled}
        setEnabled={setEnabled}
        data={data}
        isLoading={isLoading}
        totalCount={totalCount}
        pageNum={pageNum}
        pageSize={pageSize}
        onPageChange={onPageChange}
        fetchData={fetchData}
      />
    </Container>
  );
}

export default GoogleAdsCampaignsConfigContainer;
